<script lang="ts">
  // "Why?" — the required note behind a manual card action.
  //
  // A human move CONTRADICTS the resolver, which will contradict it back the moment the
  // 7-day pin lapses. The note is the only thing that makes the override reviewable
  // later, so it is a required field rather than an optional one: Save stays disabled
  // until there is something to save, and the server refuses a blank one anyway.
  //
  // ONE PROMPT FOR EVERY PATH. The board's drag-and-drop, the drawer's stage select and
  // the drawer's unpin button all raise this same component through the page, so none of
  // them can quietly acquire a way to skip the note.
  let {
    kind,
    memberName = 'this member',
    fromLabel = null,
    toLabel = null,
    busy = false,
    error = null,
    onconfirm,
    oncancel,
  }: {
    /** `move` — into another state; `pin` — re-asserting the state it is in;
     *  `unpin` — releasing the pin early. Only the wording differs. */
    kind: 'move' | 'pin' | 'unpin';
    memberName?: string;
    fromLabel?: string | null;
    toLabel?: string | null;
    busy?: boolean;
    error?: string | null;
    onconfirm: (reason: string) => void;
    oncancel: () => void;
  } = $props();

  // Mirrors the server's floor (`_REASON_MIN`). Low on purpose — it is a guard against an
  // empty box and a stray keystroke, not an essay requirement.
  const MIN = 3;

  let reason = $state('');
  const ready = $derived(reason.trim().length >= MIN && !busy);

  const title = $derived(
    kind === 'unpin' ? 'Release this pin' : kind === 'pin' ? 'Confirm this state' : 'Move this card',
  );
  const blurb = $derived(
    kind === 'unpin'
      ? `The resolver takes ${memberName}'s card back on the next sync and may move it straight back. Why are you releasing it?`
      : kind === 'pin'
        ? `${memberName} is already in ${toLabel ?? 'this state'}. Confirming pins the card for 7 days so the resolver leaves it alone. Why?`
        : `You are overriding what the signals resolved for ${memberName}. The card is pinned for 7 days, then the resolver decides again. Why the move?`,
  );

  function submit(): void {
    if (!ready) return;
    onconfirm(reason.trim());
  }

  /** Cmd/Ctrl+Enter submits — this is a textarea, so plain Enter has to stay a newline. */
  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      oncancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  class="fixed inset-0 z-[90] grid place-items-center bg-black/45 p-4"
  role="dialog"
  aria-modal="true"
  aria-label={title}
  onkeydown={onKeydown}
  tabindex="-1"
>
  <!-- Backdrop click cancels; the click must not pass through to the card underneath. -->
  <button class="absolute inset-0 cursor-default" aria-label="Cancel" onclick={oncancel}></button>

  <div class="relative w-full max-w-md rounded-box border hair bg-base-100 p-5 shadow-2xl">
    <h2 class="font-display text-base font-semibold">{title}</h2>

    {#if kind === 'move' && fromLabel && toLabel}
      <div class="mt-2 flex items-center gap-2 text-sm">
        <span class="rounded-field bg-base-content/8 px-2 py-0.5 text-base-content/72">{fromLabel}</span>
        <span class="text-base-content/40">→</span>
        <span class="rounded-field bg-primary/12 px-2 py-0.5 font-medium text-primary">{toLabel}</span>
      </div>
    {/if}

    <!-- The blurb names the member ("Marcus is already in Slipping…"), so it is
         redacted; the dialog's title above it is chrome and stays sharp. -->
    <p class="redact mt-2 text-sm leading-relaxed text-base-content/72">{blurb}</p>

    <!-- svelte-ignore a11y_autofocus -->
    <textarea
      class="textarea textarea-bordered mt-3 min-h-24 w-full bg-base-100 text-sm leading-relaxed"
      placeholder="e.g. spoke to them today — they’re coming back after their launch"
      autofocus
      bind:value={reason}
      disabled={busy}
    ></textarea>

    {#if error}
      <p class="mt-2 text-sm text-error">{error}</p>
    {/if}

    <div class="mt-4 flex items-center justify-end gap-2">
      <button class="btn btn-ghost btn-sm" onclick={oncancel} disabled={busy}>Cancel</button>
      <button class="btn btn-primary btn-sm" onclick={submit} disabled={!ready}>
        {#if busy}<span class="loading loading-spinner loading-xs"></span>{/if}
        {kind === 'unpin' ? 'Release pin' : kind === 'pin' ? 'Confirm' : 'Move card'}
      </button>
    </div>
  </div>
</div>
